import type { CourseOption, PlanCourse, RequirementGroup } from '@teachur/http-client';

import { courseKey } from './planView';

/** Turn a picker option into a Newlane plan course. */
export const optionToPlanCourse = (option: CourseOption): PlanCourse => ({
  courseId: option.courseId,
  transcriptCourseId: null,
  name: option.name,
  code: option.code,
  credits: option.credits,
  source: 'newlane',
  transferred: false,
  status: 'not_started',
});

/** All Newlane course ids currently in the plan (for picker exclusion). */
export const collectCourseIds = (groups: RequirementGroup[]): number[] =>
  groups
    .flatMap((g) => g.courses)
    .filter((c) => c.courseId != null)
    .map((c) => c.courseId as number);

/** Replace one course (matched by key) in a group with a new course. */
export const replaceCourseInGroup = (
  group: RequirementGroup,
  replacingKey: string,
  replacement: PlanCourse
): RequirementGroup => ({
  ...group,
  courses: group.courses.map((c) => (courseKey(c) === replacingKey ? replacement : c)),
});

/** Append courses to a group, skipping any already present. */
export const addCoursesToGroup = (
  group: RequirementGroup,
  additions: PlanCourse[]
): RequirementGroup => {
  const existing = new Set(group.courses.map(courseKey));
  const toAdd = additions.filter((c) => !existing.has(courseKey(c)));
  return { ...group, courses: [...group.courses, ...toAdd] };
};

/** Remove one course (matched by key) from a group. */
export const removeCourseFromGroup = (
  group: RequirementGroup,
  course: PlanCourse
): RequirementGroup => ({
  ...group,
  courses: group.courses.filter((c) => courseKey(c) !== courseKey(course)),
});

/** Apply a transform to the group with `groupId`, leaving others untouched. */
export const mapGroup = (
  groups: RequirementGroup[],
  groupId: string,
  transform: (group: RequirementGroup) => RequirementGroup
): RequirementGroup[] => groups.map((g) => (g.id === groupId ? transform(g) : g));

interface PlanLike {
  degree: { type: string };
  groups: RequirementGroup[];
}

const withGroups = <T extends PlanLike>(
  plan: T,
  groupId: string,
  transform: (group: RequirementGroup) => RequirementGroup
): T => ({ ...plan, groups: mapGroup(plan.groups, groupId, transform) });

/** Switch one course in a plan's group for a picker option. */
export const switchCourseInPlan = <T extends PlanLike>(
  plan: T,
  groupId: string,
  replacingKey: string,
  option: CourseOption
): T =>
  withGroups(plan, groupId, (g) =>
    replaceCourseInGroup(g, replacingKey, optionToPlanCourse(option))
  );

/** Add picker options to a plan's group, skipping courses already in it. */
export const addCoursesToPlan = <T extends PlanLike>(
  plan: T,
  groupId: string,
  options: CourseOption[]
): T => withGroups(plan, groupId, (g) => addCoursesToGroup(g, options.map(optionToPlanCourse)));

/** Remove one course from a plan's group. */
export const removeCourseFromPlan = <T extends PlanLike>(
  plan: T,
  groupId: string,
  course: PlanCourse
): T => withGroups(plan, groupId, (g) => removeCourseFromGroup(g, course));

/** Replace the plan with the same degree type, leaving the other plans untouched. */
export const replacePlan = <T extends PlanLike>(plans: T[], updated: T): T[] =>
  plans.map((p) => (p.degree.type === updated.degree.type ? updated : p));

/**
 * For each plan (keyed by degree type), the Newlane course ids it shares with any
 * other plan in the list. Computed from the current plans, so it reflects edits.
 */
export const sharedCourseIdsByType = (plans: PlanLike[]): Map<string, Set<number>> => {
  const byType = new Map<string, Set<number>>();
  plans.forEach((plan, index) => {
    const otherIds = new Set(
      plans.filter((_, other) => other !== index).flatMap((p) => collectCourseIds(p.groups))
    );
    byType.set(
      plan.degree.type,
      new Set(collectCourseIds(plan.groups).filter((id) => otherIds.has(id)))
    );
  });
  return byType;
};
